'use client';

import React, { useEffect, useLayoutEffect, useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';

type FormState = {
  fullName: string;
  phone: string;
  email: string;
  orderNumber: string;
  preferredDate: string;
  preferredTime: string;
  deviceType: string;
  serviceType: string;
  serviceLocation: 'Tận nhà' | 'Showroom';
  couponCode: string;
  address: string;
  note: string;
  agreed: boolean;
};

const INITIAL_FORM: FormState = {
  fullName: '',
  phone: '',
  email: '',
  orderNumber: '',
  preferredDate: '',
  preferredTime: '08:30 - 10:30',
  deviceType: 'PC để bàn',
  serviceType: 'Vệ sinh tiêu chuẩn',
  serviceLocation: 'Tận nhà',
  couponCode: '',
  address: '',
  note: '',
  agreed: false,
};

const SERVICE_PACKAGES = [
  {
    name: 'Vệ sinh tiêu chuẩn',
    subtitle: 'Nhanh - tiết kiệm - phù hợp bảo dưỡng định kỳ',
    scope: 'Bao gồm vệ sinh case và fan, thổi bụi cơ bản.',
    homePrice: '200.000đ (tận nhà)',
    showroomPrice: '100.000đ (mang máy tới showroom)',
    note: 'Phù hợp máy sử dụng hằng ngày, chưa cần tháo sâu.',
  },
  {
    name: 'Vệ sinh chuyên sâu',
    subtitle: 'Tháo rời toàn bộ - tối ưu toàn diện',
    scope: 'Tháo rời case, vệ sinh từng linh kiện, tra keo tản nhiệt, cài Win miễn phí.',
    homePrice: '350.000đ (tận nhà)',
    showroomPrice: '150.000đ (mang máy tới showroom)',
    note: 'Dành cho máy bám bụi lâu ngày, nhiệt độ cao hoặc cần bảo trì tổng thể.',
  },
  {
    name: 'Vệ sinh VGA',
    subtitle: 'Vệ sinh + thay thermal pad theo dòng vga',
    scope:
      'Áp dụng vệ sinh VGA, thay thermal pad/keo theo hiện trạng và model card (2080/3080/4070/5070...).',
    homePrice: 'Không hỗ trợ tận nhà',
    showroomPrice: '200.000đ - 1.000.000đ tại showroom',
    note: 'Chỉ thực hiện tại showroom do cần dụng cụ chuyên dụng.',
  },
] as const;

const SHOWROOM_ADDRESS = 'Số 28, Thôn 1, Xã Phú Nghĩa, Tỉnh Đồng Nai, Việt Nam';
const SHOWROOM_MAP_LINK =
  'https://www.google.com/maps/search/?api=1&query=S%E1%BB%91%2028%2C%20Th%C3%B4n%201%2C%20X%C3%A3%20Ph%C3%BA%20Ngh%C4%A9a%2C%20T%E1%BB%89nh%20%C4%90%E1%BB%93ng%20Nai%2C%20Vi%E1%BB%87t%20Nam';

function formatMoney(value: number) {
  return `${new Intl.NumberFormat('vi-VN').format(Math.max(0, value))}đ`;
}

function getServicePricing(serviceType: string, location: 'Tận nhà' | 'Showroom') {
  if (serviceType === 'Vệ sinh tiêu chuẩn') {
    const amount = location === 'Showroom' ? 100000 : 200000;
    return { amount, amountText: formatMoney(amount), note: '' };
  }
  if (serviceType === 'Vệ sinh chuyên sâu') {
    const amount = location === 'Showroom' ? 150000 : 350000;
    return { amount, amountText: formatMoney(amount), note: '' };
  }
  if (serviceType === 'Vệ sinh VGA') {
    return {
      amount: null,
      amountText: '200.000đ - 1.000.000đ',
      note: 'Giá chính xác phụ thuộc model VGA và tình trạng thermal pad.',
    };
  }
  return { amount: null, amountText: 'Liên hệ', note: '' };
}

function todayYmd() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

export default function ContactBookingPage() {
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(() => ({
    ...INITIAL_FORM,
    fullName: user?.full_name || '',
    phone: user?.phone || '',
    email: user?.email || '',
  }));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [bookingCode, setBookingCode] = useState('');
  const [couponStatus, setCouponStatus] = useState<'idle' | 'applied'>('idle');

  useLayoutEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    let stopped = false;
    let frameId = 0;
    const timeouts: number[] = [];
    const startedAt = performance.now();

    const scrollToTop = () => {
      if (stopped) return;
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      window.scrollTo(0, 0);
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    const holdTopDuringBrowserRestore = () => {
      scrollToTop();
      if (!stopped && performance.now() - startedAt < 1800) {
        frameId = window.requestAnimationFrame(holdTopDuringBrowserRestore);
      }
    };

    const releaseOnUserIntent = () => {
      stopped = true;
      if (frameId) window.cancelAnimationFrame(frameId);
      timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };

    const handlePageShow = () => {
      scrollToTop();
      timeouts.push(window.setTimeout(scrollToTop, 50));
      timeouts.push(window.setTimeout(scrollToTop, 250));
    };

    const holdTopOnAutoFocus = (event: FocusEvent) => {
      if (performance.now() - startedAt > 1800) return;
      (event.target as HTMLElement | null)?.blur?.();
      scrollToTop();
    };

    holdTopDuringBrowserRestore();
    [0, 80, 180, 360, 650, 1000, 1400, 1800].forEach((delay) => {
      timeouts.push(window.setTimeout(scrollToTop, delay));
    });

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('focusin', holdTopOnAutoFocus, true);
    window.addEventListener('wheel', releaseOnUserIntent, { passive: true, once: true });
    window.addEventListener('touchstart', releaseOnUserIntent, { passive: true, once: true });
    window.addEventListener('keydown', releaseOnUserIntent, { once: true });

    return () => {
      releaseOnUserIntent();
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('focusin', holdTopOnAutoFocus, true);
      window.removeEventListener('wheel', releaseOnUserIntent);
      window.removeEventListener('touchstart', releaseOnUserIntent);
      window.removeEventListener('keydown', releaseOnUserIntent);
    };
  }, []);

  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      fullName: prev.fullName || user?.full_name || '',
      phone: prev.phone || user?.phone || '',
      email: prev.email || user?.email || '',
    }));
  }, [user]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyOverflowY = document.body.style.overflowY;
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlOverflowY = document.documentElement.style.overflowY;

    document.body.style.overflow = '';
    document.body.style.overflowY = 'auto';
    document.documentElement.style.overflow = '';
    document.documentElement.style.overflowY = 'auto';

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      document.body.style.overflowY = prevBodyOverflowY;
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.overflowY = prevHtmlOverflowY;
    };
  }, []);

  useEffect(() => {
    setForm((prev) => {
      if (prev.serviceLocation === 'Showroom') {
        if (prev.address === SHOWROOM_ADDRESS) return prev;
        return { ...prev, address: SHOWROOM_ADDRESS };
      }
      if (prev.address === SHOWROOM_ADDRESS) {
        return { ...prev, address: '' };
      }
      return prev;
    });
  }, [form.serviceLocation]);

  const minDate = todayYmd();
  const pricing = getServicePricing(form.serviceType, form.serviceLocation);
  const isShowroom = form.serviceLocation === 'Showroom';

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    if (
      !form.fullName.trim() ||
      !form.phone.trim() ||
      !form.preferredDate ||
      !form.address.trim()
    ) {
      return 'Vui lòng điền đầy đủ các trường bắt buộc.';
    }
    if (!form.agreed) {
      return 'Vui lòng xác nhận đồng ý điều khoản đặt lịch.';
    }
    return '';
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setError('');
    setSuccess('');
    setBookingCode('');

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch('/api/maintenance-bookings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fullName: form.fullName,
          phone: form.phone,
          email: form.email,
          orderNumber: form.orderNumber,
          preferredDate: form.preferredDate,
          preferredTime: form.preferredTime,
          deviceType: form.deviceType,
          serviceType: form.serviceType,
          serviceLocation: form.serviceLocation,
          estimatedPriceText: pricing.amountText,
          couponCode: form.couponCode,
          address: form.address,
          note: form.note,
        }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.error || 'Không thể gửi lịch hẹn.');
      }

      setSuccess(payload?.message || 'Đặt lịch thành công.');
      setBookingCode(String(payload?.booking?.bookingCode || ''));
      setCouponStatus('idle');
      setForm((prev) => ({ ...INITIAL_FORM, agreed: false, email: prev.email }));
    } catch (submitError: unknown) {
      const message =
        submitError && typeof submitError === 'object' && 'message' in submitError
          ? String((submitError as { message?: string }).message || '')
          : '';
      setError(message || 'Không thể gửi lịch hẹn.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden bg-slate-50">
      <Header />
      <main className="flex-1 py-8">
        <div className="mx-auto w-full max-w-[980px] px-4">
          <nav className="mb-5 text-sm text-slate-500">
            <Link href="/" className="hover:text-primary">
              Trang chủ
            </Link>
            <span className="mx-2">/</span>
            <span className="font-medium text-slate-800">Đặt lịch vệ sinh máy</span>
          </nav>

          <section className="overflow-hidden rounded-2xl border border-amber-200/70 bg-white shadow-[0_12px_34px_rgba(148,109,0,0.08)]">
            <div className="border-b border-amber-100 bg-gradient-to-r from-amber-50 to-white px-5 py-4">
              <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-amber-700">
                Hậu mãi khách hàng
              </p>
              <h1 className="mt-1 text-2xl font-black text-slate-900">Đặt lịch vệ sinh máy</h1>
              <p className="mt-1 text-sm text-slate-600">
                Áp dụng cho khách đã từng mua máy tại DN TECH. Hệ thống sẽ kiểm tra đơn hàng trước
                khi xác nhận.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5 p-5">
              <div className="grid gap-4 md:grid-cols-2">
                <label className="text-sm font-medium text-slate-700">
                  Họ và tên <span className="text-red-500">*</span>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => updateField('fullName', e.currentTarget.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    placeholder="Nguyễn Văn A"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Số điện thoại <span className="text-red-500">*</span>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => updateField('phone', e.currentTarget.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    placeholder="09xxxxxxxx"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => updateField('email', e.currentTarget.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    placeholder="you@example.com"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Mã đơn đã mua máy (không bắt buộc)
                  <input
                    type="text"
                    value={form.orderNumber}
                    onChange={(e) =>
                      updateField('orderNumber', e.currentTarget.value.toUpperCase())
                    }
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    placeholder="DN-2026-000123"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Ngày mong muốn <span className="text-red-500">*</span>
                  <input
                    type="date"
                    min={minDate}
                    value={form.preferredDate}
                    onChange={(e) => updateField('preferredDate', e.currentTarget.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  />
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Khung giờ mong muốn <span className="text-red-500">*</span>
                  <select
                    value={form.preferredTime}
                    onChange={(e) => updateField('preferredTime', e.currentTarget.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  >
                    <option>08:30 - 10:30</option>
                    <option>10:30 - 12:00</option>
                    <option>13:30 - 15:30</option>
                    <option>15:30 - 17:30</option>
                    <option>18:00 - 20:00</option>
                  </select>
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Loại thiết bị <span className="text-red-500">*</span>
                  <select
                    value={form.deviceType}
                    onChange={(e) => updateField('deviceType', e.currentTarget.value)}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  >
                    <option>PC để bàn</option>
                    <option>Laptop</option>
                    <option>Máy trạm (Workstation)</option>
                    <option>All-in-one</option>
                  </select>
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Gói dịch vụ <span className="text-red-500">*</span>
                  <select
                    value={form.serviceType}
                    onChange={(e) => {
                      const nextService = e.currentTarget.value;
                      updateField('serviceType', nextService);
                      if (nextService === 'Vệ sinh VGA' && form.serviceLocation !== 'Showroom') {
                        updateField('serviceLocation', 'Showroom');
                      }
                    }}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  >
                    <option>Vệ sinh tiêu chuẩn</option>
                    <option>Vệ sinh chuyên sâu</option>
                    <option>Vệ sinh VGA</option>
                  </select>
                </label>

                <label className="text-sm font-medium text-slate-700">
                  Hình thức vệ sinh <span className="text-red-500">*</span>
                  <select
                    value={form.serviceLocation}
                    onChange={(e) =>
                      updateField(
                        'serviceLocation',
                        e.currentTarget.value as FormState['serviceLocation']
                      )
                    }
                    disabled={form.serviceType === 'Vệ sinh VGA'}
                    className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                  >
                    <option>Tận nhà ( bán kính 10km )</option>
                    <option>Showroom</option>
                  </select>
                  {isShowroom ? (
                    <p className="mt-1 text-xs text-amber-700">
                      Đang áp dụng địa chỉ showroom cố định.{' '}
                      <a
                        href={SHOWROOM_MAP_LINK}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-semibold underline"
                      >
                        Xem địa chỉ trên bản đồ
                      </a>
                    </p>
                  ) : null}
                </label>
              </div>

              <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
                <h2 className="text-sm font-extrabold uppercase tracking-wide text-amber-800">
                  Chi tiết gói vệ sinh
                </h2>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  {SERVICE_PACKAGES.map((pkg) => {
                    const active = form.serviceType === pkg.name;
                    return (
                      <div
                        key={pkg.name}
                        className={`rounded-lg border p-3 transition ${
                          active
                            ? 'border-amber-400 bg-white shadow-[0_8px_20px_rgba(217,119,6,0.16)]'
                            : 'border-amber-200 bg-white/80'
                        }`}
                      >
                        <p className="text-sm font-black text-slate-900">{pkg.name}</p>
                        <p className="mt-1 text-xs font-semibold text-amber-700">{pkg.subtitle}</p>
                        <p className="mt-2 text-xs text-slate-700">{pkg.scope}</p>
                        <div className="mt-2 space-y-1 rounded-md bg-slate-50 p-2 text-xs">
                          <p>
                            <span className="font-semibold text-slate-800">Tận nhà:</span>{' '}
                            {pkg.homePrice}
                          </p>
                          <p>
                            <span className="font-semibold text-slate-800">Showroom:</span>{' '}
                            {pkg.showroomPrice}
                          </p>
                        </div>
                        <p className="mt-2 text-[11px] text-slate-500">{pkg.note}</p>
                      </div>
                    );
                  })}
                </div>
                {form.serviceType === 'Vệ sinh VGA' ? (
                  <p className="mt-3 rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-xs font-semibold text-orange-700">
                    Lưu ý: Gói Vệ sinh VGA chỉ nhận xử lý tại showroom, không hỗ trợ tận nhà.
                  </p>
                ) : null}
              </div>

              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
                <p className="text-sm font-bold text-emerald-800">Chi phí tạm tính</p>
                <p className="mt-1 text-xl font-black text-emerald-700">{pricing.amountText}</p>
                <p className="mt-1 text-xs text-emerald-700">
                  Gói: <span className="font-semibold">{form.serviceType}</span> - Hình thức:{' '}
                  <span className="font-semibold">{form.serviceLocation}</span>
                </p>
                {pricing.note ? (
                  <p className="mt-1 text-xs text-emerald-700">{pricing.note}</p>
                ) : null}
                <p className="mt-1 text-[11px] text-emerald-700/90">
                  Giá cuối cùng có thể điều chỉnh theo hiện trạng thực tế khi kỹ thuật viên kiểm
                  tra.
                </p>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                <p className="text-sm font-bold text-slate-800">Mã giảm giá (nếu có)</p>
                <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                  <input
                    type="text"
                    value={form.couponCode}
                    onChange={(e) => {
                      setCouponStatus('idle');
                      updateField('couponCode', e.currentTarget.value.toUpperCase().trimStart());
                    }}
                    className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                    placeholder="VD: VESINH50K"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!form.couponCode.trim()) {
                        setError('Vui lòng nhập mã giảm giá trước khi áp dụng.');
                        return;
                      }
                      setError('');
                      setCouponStatus('applied');
                    }}
                    className="rounded-lg border border-amber-300 bg-white px-4 py-2.5 text-sm font-bold text-amber-700 transition hover:bg-amber-50"
                  >
                    Áp mã
                  </button>
                </div>
                <p className="mt-1 text-xs text-slate-500">
                  Mã giảm giá sẽ được kỹ thuật viên kiểm tra và xác nhận khi chốt lịch.
                </p>
                {couponStatus === 'applied' ? (
                  <p className="mt-2 text-xs font-semibold text-emerald-700">
                    Đã áp dụng mã: {form.couponCode}
                  </p>
                ) : null}
              </div>

              <label className="block text-sm font-medium text-slate-700">
                Địa chỉ nhận máy / showroom mong muốn <span className="text-red-500">*</span>
                <input
                  type="text"
                  value={form.address}
                  onChange={(e) => updateField('address', e.currentTarget.value)}
                  disabled={isShowroom}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-slate-100"
                  placeholder="Ví dụ: Số 28, Thôn 1, Xã Phú Nghĩa, Tỉnh Đồng Nai, Việt Nam"
                />
              </label>

              <label className="block text-sm font-medium text-slate-700">
                Ghi chú thêm
                <textarea
                  rows={4}
                  value={form.note}
                  onChange={(e) => updateField('note', e.currentTarget.value)}
                  className="mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  placeholder="Mô tả hiện trạng máy, tiếng ồn quạt, nhiệt độ cao..."
                />
              </label>

              <label className="flex items-start gap-2 text-sm text-slate-600">
                <input
                  type="checkbox"
                  checked={form.agreed}
                  onChange={(e) => updateField('agreed', e.currentTarget.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-slate-300 text-amber-500 focus:ring-amber-200"
                />
                <span>
                  Tôi xác nhận thông tin đúng sự thật và đồng ý để DN TECH liên hệ xác nhận lịch.
                </span>
              </label>

              {error ? (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {error}
                </div>
              ) : null}

              {success ? (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {success}
                  {bookingCode ? (
                    <span className="ml-1 font-bold">Mã lịch: {bookingCode}</span>
                  ) : null}
                </div>
              ) : null}

              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                <Link
                  href="/"
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
                >
                  Quay về trang chủ
                </Link>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center rounded-lg bg-amber-500 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? 'Đang gửi...' : 'Gửi lịch hẹn'}
                </button>
              </div>
            </form>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
